/** Shared login-page blue theme for Gap Assessment vendor pages */

export const GAP_ORG_SEARCH_KEY = "gap-assessment-org-search";

export function getOrgSearch() {
  try {
    return sessionStorage.getItem(GAP_ORG_SEARCH_KEY) || "";
  } catch {
    return "";
  }
}

export function setOrgSearch(value) {
  try {
    const v = (value || "").trim();
    if (v) sessionStorage.setItem(GAP_ORG_SEARCH_KEY, v);
    else sessionStorage.removeItem(GAP_ORG_SEARCH_KEY);
  } catch {
    /* ignore */
  }
}

export const LOGIN_PAGE_BG = `
  radial-gradient(ellipse 80% 60% at 20% 15%, rgba(99,102,241,0.55) 0%, transparent 55%),
  radial-gradient(ellipse 60% 70% at 80% 85%, rgba(14,165,233,0.42) 0%, transparent 55%),
  linear-gradient(160deg, #0a1628 0%, #0d2145 40%, #0f2d6b 70%, #112366 100%)`;

export const STAT_CARD_BG = `
  radial-gradient(ellipse 90% 70% at 15% 10%, rgba(99,102,241,0.22) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 85% 90%, rgba(14,165,233,0.16) 0%, transparent 55%),
  linear-gradient(160deg, #e8f2fc 0%, #dceaf8 42%, #d0e2f5 100%)`;

const STAT_ACCENT_DARK = {
  "#7dd3fc": "#0369a1",
  "#a5b4fc": "#4338ca",
  "#6ee7b7": "#047857",
  "#34d399": "#047857",
  "#fcd34d": "#b45309",
  "#fca5a5": "#b91c1c",
  "#94a3b8": "#334155",
};

function statAccentDark(accent) {
  return STAT_ACCENT_DARK[accent?.toLowerCase()] || "#1e40af";
}

export const DOT_GRID = {
  backgroundImage:
    "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)",
  backgroundSize: "32px 32px",
};

/** Same palette as GapAssessment ScoreChip / ResultScreen */
export function scoreColor(s) {
  return s >= 75 ? "#10b981" : s >= 50 ? "#f59e0b" : "#ef4444";
}

export function scoreLabel(s) {
  return s >= 75 ? "Compliant" : s >= 50 ? "Partial" : "Critical";
}

export function scoreBg(s) {
  const c = scoreColor(s);
  return `${c}18`;
}

export function ThemeShell({ children, style = {} }) {
  return (
    <div style={{ position: "relative", overflow: "hidden", ...style }}>
      <div
        style={{ position: "absolute", inset: 0, background: LOGIN_PAGE_BG }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          ...DOT_GRID,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 280,
          height: 280,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.22)",
          filter: "blur(60px)",
          top: -80,
          left: -60,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "rgba(14,165,233,0.18)",
          filter: "blur(55px)",
          bottom: -70,
          right: -40,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </div>
  );
}

export function GlassStatCard({ tag, title, value, subtitle, accent, icon: Icon }) {
  const iconColor = statAccentDark(accent);

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 12,
        border: "1px solid rgba(99,102,241,0.2)",
        minHeight: 56,
        boxShadow: "0 2px 8px rgba(99,102,241,0.08)",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: STAT_CARD_BG }} />
      <div
        style={{
          position: "absolute",
          width: 110,
          height: 110,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.1)",
          filter: "blur(36px)",
          top: -40,
          right: -20,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: "rgba(255,255,255,0.45)",
        }}
      >
        {Icon && (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              flexShrink: 0,
              background: "#ffffff",
              border: `1px solid ${iconColor}35`,
              boxShadow: "0 1px 4px rgba(15,23,42,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={14} color={iconColor} strokeWidth={2.25} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#0f172a",
                lineHeight: 1,
              }}
            >
              {value}
            </span>
            {tag && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  color: iconColor,
                }}
              >
                /{tag}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#1e293b",
              marginTop: 2,
              lineHeight: 1.2,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#475569",
                marginTop: 1,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
